import { useEffect } from "react";
import { Link } from "react-router-dom";
import { knowledgePageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

export default function Knowledge() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="逆齡知識庫｜時光逆行駭客｜醫學美容健康觀念指南"
        description="逆齡知識庫整理曾驛翔院長在看診與衛教過程中常被問到的問題與觀念。從男性懸崖式衰老、青春痘照護到熟齡慢老，建立正確觀念再開始行動。"
        keywords="逆齡知識,醫學美容觀念,抗衰老知識,曾驛翔,時光逆行駭客,健康管理"
        canonicalPath="/knowledge"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "逆齡知識庫｜時光逆行駭客",
          "description": "曾驛翔院長的逆齡觀念與常見迷思破解，建立正確觀念再開始行動。",
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg">
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center reveal">
            <span className="section-label">{knowledgePageData.heroTagline}</span>
            <h1 className="mt-4 text-3xl md:text-5xl font-bold text-ghost leading-tight">
              {knowledgePageData.heroTitle}
              <br className="hidden md:block" />
              <span className="text-gradient-gold">{knowledgePageData.heroTitleAccent}</span>
            </h1>
            <p className="mt-6 text-ghost-muted text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              {knowledgePageData.heroDesc}
            </p>
            <div className="mt-8 inline-flex items-start gap-3 bg-neon-cyan/10 border border-neon-cyan/20 rounded-lg px-5 py-4 max-w-3xl mx-auto text-left">
              <i className="ri-error-warning-line text-neon-cyan text-xl shrink-0 mt-0.5" />
              <p className="text-ghost-muted text-base leading-relaxed">
                {knowledgePageData.heroNote}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 md:py-24">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal">
            <span className="section-label">{knowledgePageData.categories.label}</span>
            <h2 className="mt-3 text-2xl md:text-4xl font-bold text-ghost">
              {knowledgePageData.categories.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {knowledgePageData.categories.items.map((cat, idx) => (
              <Link
                key={idx}
                to={cat.path}
                className="card-cyber p-6 md:p-8 group reveal block"
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: `${cat.accent}15` }}
                >
                  <i className={`${cat.icon} text-xl`} style={{ color: cat.accent }} />
                </div>
                <h3 className="text-ghost font-bold text-lg mb-3 group-hover:text-cyber-gold transition-colors">
                  {cat.title}
                </h3>
                <p className="text-ghost-muted text-base leading-relaxed mb-4">{cat.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-base font-medium" style={{ color: cat.accent }}>
                  了解更多 <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Myths & FAQ */}
      <section className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal">
            <span className="section-label">{knowledgePageData.myths.label}</span>
            <h2 className="mt-3 text-2xl md:text-4xl font-bold text-ghost">
              {knowledgePageData.myths.title}
            </h2>
          </div>
          <div className="space-y-6 max-w-4xl mx-auto">
            {knowledgePageData.myths.items.map((myth, idx) => (
              <div
                key={idx}
                className="card-cyber p-6 md:p-8 reveal"
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div className="flex items-start gap-3 mb-4">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-cyber-gold/10 flex items-center justify-center">
                    <i className="ri-question-mark text-cyber-gold text-base" />
                  </span>
                  <h3 className="text-ghost font-bold text-base md:text-lg leading-relaxed">
                    {myth.q}
                  </h3>
                </div>
                <p className="text-ghost-muted text-base leading-relaxed pl-11">
                  {myth.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 md:py-24 border-t border-abyss-800">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: 1280 }}>
          <div className="text-center reveal">
            <span className="section-label">{knowledgePageData.bottomCta.label}</span>
            <h2 className="mt-4 text-2xl md:text-4xl font-bold text-ghost">
              {knowledgePageData.bottomCta.title}
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={lineUrl} target="_blank" rel="noopener noreferrer" className="btn-line whitespace-nowrap">
                <i className="ri-line-fill" />
                {knowledgePageData.bottomCta.ctaLine}
              </a>
              <Link to="/booking" className="btn-gold whitespace-nowrap">
                {knowledgePageData.bottomCta.ctaBooking}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}